/**
 * Pure helpers for the brief-template editor draft state. The page composes
 * these into hydration, dirty detection, and the Save payload mapper.
 *
 * Sections are diffed by id on the server (`BriefTemplatesService.update`), so
 * `toCreateDto` emits `id: draft.serverId ?? undefined` — the line that makes the
 * id-keyed diff work end to end.
 */
import type { BriefTemplateSectionResponseDto } from '@inference.engine/assets-api-client/web';
import { v4 as uuidv4 } from 'uuid';
import type { UpdateBriefTemplateSectionVariables } from '@/entities/brief-template';
import {
  DEFAULT_SECTION_CONFIG,
  resolveBriefSectionType,
  type BriefSectionType,
} from './sectionTypes';

/**
 * Mirrors a section row while the user edits the template. Tombstoned rows
 * (`isDeleted: true`) stay in the array for stable React keys until Save,
 * then get stripped from the outgoing payload.
 */
export interface SectionDraft {
  /** Stable id for React keys + dnd-kit. Server id for existing rows, generated for new rows. */
  tempId: string;
  /** Server id when the row originated from the API; `null` for unsaved new rows. */
  serverId: string | null;
  name: string;
  required: boolean;
  aiAllowed: boolean;
  /** TipTap doc passed through verbatim — rich-text editor lands in a later ticket. */
  content: Record<string, unknown> | undefined;
  position: number;
  /** Per-section guidance for the brief author. `null` = absent. */
  instructions: string | null;
  /** The typed field this section renders as. */
  type: BriefSectionType;
  /** Per-type configuration; shape depends on `type`. */
  config: Record<string, unknown>;
  /** Whether the author may attach files to this section. */
  allowFiles: boolean;
  /** AI generation instructions. `null` = not yet authored — see `resolveSectionAiInstructions`. */
  aiInstructions: string | null;
  isNew: boolean;
  isDeleted: boolean;
}

/**
 * Drops a leading "1." / "1)" prefix from a section name. Anchored to the
 * leading position so mid-name numbers ("Section 1: Overview") survive.
 * Idempotent — repeat calls are no-ops once the prefix is gone.
 */
export function stripLeadingSectionNumber(name: string): string {
  return name.replace(/^\s*\d+[.)]\s+/, '').trim();
}

export function normalizeAiText(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

/**
 * The AI instructions a section effectively carries: the authored field once it exists, the
 * section's description until then. The drawer shows this fallback, so the Save gate and the
 * outgoing payload must read the same expression — otherwise the field reads filled while
 * validation calls it empty, and the server stores nothing while the author was shown text.
 *
 * Only a `null` field falls back. A blank the author typed is authored, so clearing the field
 * empties it instead of silently restoring the description under the cursor.
 */
export function resolveSectionAiInstructions(
  section: Pick<SectionDraft, 'aiInstructions' | 'instructions'>,
): string | null {
  return section.aiInstructions ?? section.instructions;
}

/**
 * `config`/`allowFiles` read through an `unknown`-typed view of the same object rather than
 * `server.config`/`server.allowFiles` directly: the generated DTO type declares both required,
 * so a deployed backend that predates this migration and omits them entirely is exactly the
 * gap a plain `??` can't see — `tsc` (correctly, per that type) would call the fallback dead,
 * which is the same bare-cast defect this function exists to close.
 */
interface PartialSectionResponse {
  config?: unknown;
  allowFiles?: unknown;
}

export function toSectionDraft(server: BriefTemplateSectionResponseDto): SectionDraft {
  // A deployed backend that predates the typed-sections migration omits `type`/`config`/
  // `allowFiles` entirely (not `null`) — the frontend must tolerate that, since it is the
  // normal state whenever the frontend deploys ahead of the backend. Resolve `type` first so
  // an absent or unrecognized `config` can default from the resolved type, not the raw one.
  const type = resolveBriefSectionType(server.type);
  const partial = server as unknown as PartialSectionResponse;
  const config =
    typeof partial.config === 'object' && partial.config !== null
      ? (partial.config as Record<string, unknown>)
      : { ...DEFAULT_SECTION_CONFIG[type] };
  const allowFiles = typeof partial.allowFiles === 'boolean' ? partial.allowFiles : false;
  return {
    tempId: server.id,
    serverId: server.id,
    name: stripLeadingSectionNumber(server.name),
    required: server.required,
    aiAllowed: server.aiAllowed,
    content: server.content,
    position: server.position,
    instructions: server.instructions ?? null,
    type,
    config,
    allowFiles,
    aiInstructions: server.aiInstructions ?? null,
    isNew: false,
    isDeleted: false,
  };
}

/**
 * Maps a draft (filtered to live rows by the caller) to the BE update-DTO.
 * `position` is rebound to the caller's array index so reorder + insert + delete
 * always emit a contiguous 0..N sequence regardless of the staged `position`.
 */
export function toCreateDto(
  draft: SectionDraft,
  index: number,
): UpdateBriefTemplateSectionVariables {
  const aiInstructions = normalizeAiText(resolveSectionAiInstructions(draft));
  return {
    id: draft.serverId ?? undefined,
    name: stripLeadingSectionNumber(draft.name),
    position: index,
    required: draft.required,
    aiAllowed: draft.aiAllowed,
    content: draft.content,
    instructions: draft.instructions?.trim() || null,
    type: draft.type,
    config: draft.config,
    allowFiles: draft.allowFiles,
    // The resolved value, not the raw field: the drawer showed the description as this
    // section's AI instructions, so that is what has to reach the server. Omitted, never sent
    // as an explicit null, when nothing resolves — the server treats a present-but-blank
    // aiInstructions as an assertion needing text. See assert-ai-instructions-present.ts.
    ...(aiInstructions !== null ? { aiInstructions } : {}),
  };
}

/**
 * Own-key comparison for `config` objects. Not `JSON.stringify` — key order is
 * incidental to how a config was built and must not produce phantom dirty state.
 */
export function configEquals(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined,
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const aKeys = Object.keys(left);
  const bKeys = Object.keys(right);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.is(left[key], right[key]) || deepEqual(left[key], right[key]));
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => deepEqual(item, b[index]));
  }
  if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
    return configEquals(a as Record<string, unknown>, b as Record<string, unknown>);
  }
  return false;
}

/**
 * Equality used by dirty detection. Tombstoned rows are real changes (delete
 * is a save-time mutation), so they're compared on the `isDeleted` axis too.
 * Order matters — reorder is dirty even if every field matches by tempId.
 */
export function shallowSectionsEqual(
  a: readonly SectionDraft[],
  b: readonly SectionDraft[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (left.tempId !== right.tempId) return false;
    if (left.name !== right.name) return false;
    if (left.required !== right.required) return false;
    if (left.aiAllowed !== right.aiAllowed) return false;
    if (left.instructions !== right.instructions) return false;
    if (left.isDeleted !== right.isDeleted) return false;
    if (left.type !== right.type) return false;
    if (left.allowFiles !== right.allowFiles) return false;
    if (left.aiInstructions !== right.aiInstructions) return false;
    if (!configEquals(left.config, right.config)) return false;
  }
  return true;
}

export function createTempId(): string {
  return `new-${uuidv4()}`;
}

/**
 * Factory for a freshly-added row. Defaults match BE defaults + "AI allowed
 * unless disallowed" semantics. `type` defaults to `text` (the entity/column
 * default); the type picker always passes an explicit type.
 */
export function createBlankDraft(nextIndex: number, type: BriefSectionType = 'text'): SectionDraft {
  return {
    tempId: createTempId(),
    serverId: null,
    name: '',
    required: false,
    // Off, as the prototype creates it. On would make the drawer open already showing the
    // "AI instructions are required" error, before the author has touched anything.
    aiAllowed: false,
    content: undefined,
    position: nextIndex,
    instructions: null,
    type,
    config: { ...DEFAULT_SECTION_CONFIG[type] },
    allowFiles: false,
    aiInstructions: null,
    isNew: true,
    isDeleted: false,
  };
}
